import { css } from '@emotion/react';
import React, { useCallback, useRef } from 'react';
import { UIColorPalette } from './ColorPalette';
import LobbyButton from './LobbyButton';

import DiscordLogo from '../assets/discord-logo-blue.svg';
import GreekPattern from '../assets/greek-pattern.svg';

function HomeInfo() {
  return (
    <div css={HomeInfoStyles.container}>
      <span css={HomeInfoStyles.sectionHeader}>
        Mythos: Ascension meets poker
      </span>
      <span css={HomeInfoStyles.sectionBody}>
        <p>
          Build your republic by purchasing cards from a shared trade row in
          rounds of secret auctions.
        </p>
        <br />
        <p>
          The trick is that you're constantly trying to read other players'
          intentions and decide how much military to commit - do you go all-in
          on that powerful card, or let someone else overpay while you snag
          cheaper options? Will you curry Favor with the Gods by paying tribute
          to their fleeting whims, crafting a city to appease your desired God,
          or plotting your course through prophecies?
        </p>
      </span>
      <span
        css={[
          LoginPageStyles.sectionHeader,
          LoginPageStyles.sectionHeaderSmall,
        ]}
      >
        Under Development
      </span>
      <span css={HomeInfoStyles.sectionBody}>
        <p>
          The game is fully functional, come play with your friends or against
          bots! We’re still getting official artwork made and tuning the balance
          of cards and mechanics. We’d love your input as we polish.
        </p>
      </span>
      <span
        css={[
          LoginPageStyles.sectionHeader,
          LoginPageStyles.sectionHeaderSmall,
        ]}
      >
        Join Our Community
      </span>
      <span css={HomeInfoStyles.sectionBody}>
        <p>
          Chat with the devs, meet other players, and help us improve the game
          by joining our{' '}
          <a href="https://discord.gg/pUhZVTKX">Discord Server</a>.
        </p>
      </span>
    </div>
  );
}

const HomeInfoStyles = {
  container: css({
    marginTop: 40,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 820,
    backgroundColor: UIColorPalette.lobbyDarkBG,
  }),
  sectionHeader: css({
    paddingTop: 16,
    paddingBottom: 16,
    textAlign: 'center',
    color: UIColorPalette.lobbyDarkText,
    backgroundColor: UIColorPalette.lobbyDarkerBG,
    borderBottom: `1px solid ${UIColorPalette.lobbyDarkBorder}`,
    backgroundImage: `url(${GreekPattern})`,
    backgroundRepeat: 'repeat',
    backgroundPosition: 'center',
    fontWeight: 'bold',
    fontFamily: 'Mythos-CardTitle',
    letterSpacing: '0.07em',
    fontSize: '1.2em',
  }),
  sectionHeaderSmall: css({
    padding: 10,
    backgroundImage: 'None',
  }),
  sectionBody: css({
    padding: 25,
    color: UIColorPalette.loginPageDescriptionText,
    textAlign: 'left',
    fontSize: '1.2em',
    lineHeight: '1.2em',
  }),
};

function LoginPage(props: { redirect?: string | null }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [requestInProgress, setRequestInProgress] = React.useState(false);

  const onGuestLoginSubmit = useCallback(
    async (event: React.BaseSyntheticEvent) => {
      event.preventDefault();
      if (requestInProgress) {
        return;
      }
      setRequestInProgress(true);
      var username = inputRef.current?.value.trim();
      if (!username) {
        return;
      }
      const res = await fetch('/auth/guest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          password: 'abc123',
          redirect: props.redirect || undefined,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        setRequestInProgress(false);
        throw new Error('error logging in: ' + text);
      }
      const body = await res.json();
      window.location.replace(body.redirect);
    },
    [requestInProgress, setRequestInProgress, inputRef, props.redirect],
  );

  const discordAuthUrl = props.redirect
    ? `/auth/discord?redirect=${encodeURIComponent(props.redirect)}`
    : '/auth/discord';
  const googleAuthUrl = props.redirect
    ? `/auth/google?redirect=${encodeURIComponent(props.redirect)}`
    : '/auth/google';

  return (
    <div css={LoginPageStyles.sharedContainer}>
      <div css={LoginPageStyles.container}>
        <span css={LoginPageStyles.sectionHeader}>
          Login to save your stats
        </span>
        <span css={LoginPageStyles.oauthSection}>
          <button
            css={LoginPageStyles.discordButton}
            type="button"
            onClick={() => (window.location.href = discordAuthUrl)}
          >
            Login with{' '}
            <img css={LoginPageStyles.discordLogo} src={DiscordLogo} />
          </button>
          <LoginWithGoogleButton href={googleAuthUrl} />
        </span>
        <span
          css={[
            LoginPageStyles.sectionHeader,
            LoginPageStyles.sectionHeaderSmall,
          ]}
        >
          Continue as Guest
        </span>
        <div css={LoginPageStyles.guestSection}>
          <form
            css={LoginPageStyles.loginUsernameForm}
            onSubmit={onGuestLoginSubmit}
          >
            <span css={LoginPageStyles.loginUsernameFormText}> Name: </span>
            <input
              css={LoginPageStyles.loginUsernameInput}
              type="text"
              ref={inputRef}
              maxLength={12}
            />
            <LobbyButton onClick={onGuestLoginSubmit}>
              Login as guest
            </LobbyButton>
          </form>
        </div>
      </div>
      <HomeInfo />
    </div>
  );
}

const LoginPageStyles = {
  sharedContainer: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }),
  container: css({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 820,
    width: '100%',
    margin: 'auto',
    backgroundColor: UIColorPalette.lobbyDarkBG,
  }),
  sectionHeader: css({
    padding: 14,
    textAlign: 'center',
    color: UIColorPalette.lobbyDarkText,
    backgroundColor: UIColorPalette.lobbyDarkerBG,
    borderBottom: `1px solid ${UIColorPalette.lobbyDarkBorder}`,
    backgroundImage: `url(${GreekPattern})`,
    backgroundRepeat: 'repeat',
    backgroundPosition: 'center',
    fontWeight: 'bold',
    fontFamily: 'Mythos-CardTitle',
    letterSpacing: '0.07em',
    fontSize: '1.2em',
  }),
  sectionHeaderSmall: css({
    padding: 10,
    backgroundImage: 'None',
  }),
  oauthSection: css({
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    margin: 20,
    gap: 6,
  }),
  discordButton: css({
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 20,
    paddingRight: 20,
    backgroundColor: UIColorPalette.loginDiscordButtonBG,
    borderRadius: 4,
    border: 'none',
    cursor: 'pointer',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    color: UIColorPalette.discordBlue,
  }),
  discordLogo: css({
    height: 18,
    width: 'auto',
    verticalAlign: 'middle',
    marginBottom: 0,
    marginLeft: 1,
  }),
  guestSection: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 15,
  }),
  loginUsernameForm: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  loginUsernameInput: css({
    maxWidth: 200,
  }),
  loginUsernameFormText: css({
    display: 'flex',
    alignItems: 'center',
    paddingRight: 5,
  }),
};

function LoginWithGoogleButton({ href }: { href: string }) {
  const handleClick = () => {
    window.location.href = href;
  };

  return (
    <button className="gsi-material-button" onClick={handleClick}>
      <div className="gsi-material-button-state"></div>
      <div className="gsi-material-button-content-wrapper">
        <div className="gsi-material-button-icon">
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            style={{ display: 'block' }}
          >
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            ></path>
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            ></path>
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            ></path>
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            ></path>
            <path fill="none" d="M0 0h48v48H0z"></path>
          </svg>
        </div>
        <span className="gsi-material-button-contents">
          Sign in with Google
        </span>
        <span style={{ display: 'none' }}>Sign in with Google</span>
      </div>
    </button>
  );
}

export default LoginPage;
